import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    TrendingUp,
    Leaf,
    Zap,
    DollarSign,
    Target,
    BarChart3,
    LineChart,
    PieChart,
    Activity
} from 'lucide-react';

const CleanBackground = () => {
    return (
        <div className="fixed inset-0 overflow-hidden pointer-events-none bg-gray-50">
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-gray-300 rounded-full transform rotate-12"></div>
                <div className="absolute top-1/3 right-1/3 w-40 h-40 bg-gray-300 rounded-full transform -rotate-6"></div>
                <div className="absolute bottom-1/3 left-1/2 w-36 h-36 bg-gray-300 rounded-full transform rotate-3"></div>
                <div className="absolute top-1/2 right-1/4 w-28 h-28 bg-gray-300 rounded-full transform -rotate-12"></div>
            </div>
        </div>
    );
};

export default function TrackImpact() {
    const [selectedTimeframe, setSelectedTimeframe] = useState('all');
    const [isLoading, setIsLoading] = useState(true);

    // Different data sets for each timeframe
    const timeframeData = {
        all: {
            co2Saved: "1,250,000 kg",
            energyProduced: "2,500,000 kWh",
            payoutsDistributed: "$450,000",
            co2Change: "+25%",
            energyChange: "+18%",
            payoutsChange: "+22%",
            projectsChange: "+3",
            totalCO2Saved: 1250000,
            totalEnergyProduced: 2500000,
            totalPayouts: 450000,
            activeProjects: 12,
            totalInvestors: 15420,
            averageReturn: 12.5
        },
        today: {
            co2Saved: "2,847 kg",
            energyProduced: "5,420 kWh",
            payoutsDistributed: "$12,450",
            co2Change: "+12%",
            energyChange: "+8%",
            payoutsChange: "+15%",
            projectsChange: "+2",
            totalCO2Saved: 2847,
            totalEnergyProduced: 5420,
            totalPayouts: 12450,
            activeProjects: 12,
            totalInvestors: 15420,
            averageReturn: 12.5
        },
        week: {
            co2Saved: "18,920 kg",
            energyProduced: "37,840 kWh",
            payoutsDistributed: "$89,200",
            co2Change: "+15%",
            energyChange: "+12%",
            payoutsChange: "+18%",
            projectsChange: "+1",
            totalCO2Saved: 18920,
            totalEnergyProduced: 37840,
            totalPayouts: 89200,
            activeProjects: 12,
            totalInvestors: 15420,
            averageReturn: 12.5
        },
        month: {
            co2Saved: "84,500 kg",
            energyProduced: "169,000 kWh",
            payoutsDistributed: "$398,000",
            co2Change: "+20%",
            energyChange: "+16%",
            payoutsChange: "+24%",
            projectsChange: "+2",
            totalCO2Saved: 84500,
            totalEnergyProduced: 169000,
            totalPayouts: 398000,
            activeProjects: 12,
            totalInvestors: 15420,
            averageReturn: 12.5
        },
        year: {
            co2Saved: "1,250,000 kg",
            energyProduced: "2,500,000 kWh",
            payoutsDistributed: "$450,000",
            co2Change: "+25%",
            energyChange: "+18%",
            payoutsChange: "+22%",
            projectsChange: "+3",
            totalCO2Saved: 1250000,
            totalEnergyProduced: 2500000,
            totalPayouts: 450000,
            activeProjects: 12,
            totalInvestors: 15420,
            averageReturn: 12.5
        }
    };

    // Get current data based on selected timeframe
    const currentData = timeframeData[selectedTimeframe as keyof typeof timeframeData];

    const realTimeMetrics = [
        {
            title: selectedTimeframe === 'today' ? "CO₂ Saved Today" :
                selectedTimeframe === 'week' ? "CO₂ Saved This Week" :
                    selectedTimeframe === 'month' ? "CO₂ Saved This Month" :
                        selectedTimeframe === 'year' ? "CO₂ Saved This Year" : "Total CO₂ Saved",
            value: currentData.co2Saved,
            change: currentData.co2Change,
            trend: "up",
            icon: Leaf,
            color: "green"
        },
        {
            title: selectedTimeframe === 'today' ? "Energy Produced Today" :
                selectedTimeframe === 'week' ? "Energy Produced This Week" :
                    selectedTimeframe === 'month' ? "Energy Produced This Month" :
                        selectedTimeframe === 'year' ? "Energy Produced This Year" : "Total Energy Produced",
            value: currentData.energyProduced,
            change: currentData.energyChange,
            trend: "up",
            icon: Zap,
            color: "blue"
        },
        {
            title: selectedTimeframe === 'today' ? "Payouts Distributed Today" :
                selectedTimeframe === 'week' ? "Payouts Distributed This Week" :
                    selectedTimeframe === 'month' ? "Payouts Distributed This Month" :
                        selectedTimeframe === 'year' ? "Payouts Distributed This Year" : "Total Payouts Distributed",
            value: currentData.payoutsDistributed,
            change: currentData.payoutsChange,
            trend: "up",
            icon: DollarSign,
            color: "green"
        },
        {
            title: "Active Projects",
            value: currentData.activeProjects.toString(),
            change: currentData.projectsChange,
            trend: "up",
            icon: Target,
            color: "purple"
        }
    ];

    const projectPerformance = [
        {
            name: "Solar Farm Karachi",
            energyProduced: 450000,
            co2Saved: 225000,
            payout: 85000,
            efficiency: 94
        },
        {
            name: "Wind Energy Hub",
            energyProduced: 380000,
            co2Saved: 190000,
            payout: 72000,
            efficiency: 89
        },
        {
            name: "Community Solar",
            energyProduced: 280000,
            co2Saved: 140000,
            payout: 52000,
            efficiency: 92
        },
        {
            name: "Hydroelectric Plant",
            energyProduced: 320000,
            co2Saved: 160000,
            payout: 61000,
            efficiency: 87
        }
    ];

    // Environmental impact data based on timeframe
    const getEnvironmentalImpact = (timeframe: string) => {
        const baseData = {
            all: { trees: "52,000", cars: "2,847", homes: "1,250", barrels: "8,500" },
            year: { trees: "52,000", cars: "2,847", homes: "1,250", barrels: "8,500" },
            month: { trees: "3,500", cars: "190", homes: "84", barrels: "570" },
            week: { trees: "800", cars: "43", homes: "19", barrels: "130" },
            today: { trees: "115", cars: "6", homes: "3", barrels: "18" }
        };

        const data = baseData[timeframe as keyof typeof baseData] || baseData.all;

        return [
            {
                metric: "Trees Planted Equivalent",
                value: data.trees,
                description: "CO₂ offset equivalent to planting trees"
            },
            {
                metric: "Cars Off Road",
                value: data.cars,
                description: "Equivalent to removing cars from roads annually"
            },
            {
                metric: "Homes Powered",
                value: data.homes,
                description: "Average homes powered by clean energy"
            },
            {
                metric: "Oil Barrels Saved",
                value: data.barrels,
                description: "Equivalent barrels of oil not consumed"
            }
        ];
    };

    const environmentalImpact = getEnvironmentalImpact(selectedTimeframe);

    useEffect(() => {
        // Simulate loading
        const timer = setTimeout(() => setIsLoading(false), 1000);
        return () => clearTimeout(timer);
    }, []);

    // Handle timeframe change with loading state
    const handleTimeframeChange = (timeframe: string) => {
        setIsLoading(true);
        setSelectedTimeframe(timeframe);
        // Simulate data loading
        setTimeout(() => setIsLoading(false), 500);
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading impact data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <CleanBackground />

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                {/* Header Section */}
                <div className="text-center mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
                            Track Your <span className="text-green-600">Impact</span>
                        </h1>
                        <p className="text-lg max-w-3xl mx-auto text-gray-600 mb-8">
                            Live dashboards showing CO₂ saved, kWh produced, and payouts distributed.
                            See the real impact of your clean energy investments.
                        </p>
                    </motion.div>

                    {/* Timeframe Filter */}
                    <motion.div
                        className="flex justify-center gap-4 mb-12"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                    >
                        {['all', 'today', 'week', 'month', 'year'].map((timeframe) => (
                            <button
                                key={timeframe}
                                onClick={() => handleTimeframeChange(timeframe)}
                                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${selectedTimeframe === timeframe
                                    ? 'bg-green-600 text-white shadow-lg'
                                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow-sm border border-gray-200'
                                    }`}
                            >
                                {timeframe.charAt(0).toUpperCase() + timeframe.slice(1)}
                            </button>
                        ))}
                    </motion.div>
                </div>

                {/* Real-time Metrics */}
                <motion.div
                    className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                >
                    {realTimeMetrics.map((metric, index) => {
                        const Icon = metric.icon;
                        return (
                            <motion.div
                                key={metric.title}
                                className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.6 + index * 0.1, duration: 0.6 }}
                                whileHover={{ y: -5, scale: 1.02 }}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className={`p-3 rounded-full ${metric.color === 'green' ? 'bg-green-100' :
                                        metric.color === 'blue' ? 'bg-blue-100' :
                                            metric.color === 'purple' ? 'bg-purple-100' : 'bg-gray-100'
                                        }`}>
                                        <Icon className={`w-6 h-6 ${metric.color === 'green' ? 'text-green-600' :
                                            metric.color === 'blue' ? 'text-blue-600' :
                                                metric.color === 'purple' ? 'text-purple-600' : 'text-gray-600'
                                            }`} />
                                    </div>
                                    <div className={`flex items-center text-sm font-medium ${metric.trend === 'up' ? 'text-green-600' : 'text-red-600'
                                        }`}>
                                        <TrendingUp className="w-4 h-4 mr-1" />
                                        {metric.change}
                                    </div>
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">{metric.value}</h3>
                                <p className="text-gray-600 text-sm">{metric.title}</p>
                            </motion.div>
                        );
                    })}
                </motion.div>

                {/* Main Impact Dashboard */}
                <div className="grid lg:grid-cols-3 gap-8 mb-16">
                    {/* CO₂ Savings Chart */}
                    <motion.div
                        className="lg:col-span-2 bg-white p-8 rounded-2xl shadow-lg border border-gray-200"
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.8, duration: 0.6 }}
                    >
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">CO₂ Savings Over Time</h2>
                            <div className="flex items-center text-green-600">
                                <Leaf className="w-5 h-5 mr-2" />
                                <span className="font-medium">Live Data</span>
                            </div>
                        </div>

                        {/* Mock Chart */}
                        <div className="h-64 bg-gradient-to-br from-green-50 to-blue-50 rounded-lg flex items-end justify-between p-4">
                            {[65, 78, 82, 75, 88, 92, 85, 95, 98, 100].map((height, index) => (
                                <div
                                    key={index}
                                    className="bg-green-600 rounded-t-lg transition-all duration-500 hover:bg-green-700"
                                    style={{
                                        height: `${height}%`,
                                        width: '8%',
                                        animationDelay: `${index * 0.1}s`
                                    }}
                                ></div>
                            ))}
                        </div>

                        <div className="mt-4 text-center">
                            <div className="text-3xl font-bold text-green-600 mb-2">
                                {currentData.totalCO2Saved.toLocaleString()} kg
                            </div>
                            <p className="text-gray-600">
                                {selectedTimeframe === 'today' ? 'CO₂ Saved Today' :
                                    selectedTimeframe === 'week' ? 'CO₂ Saved This Week' :
                                        selectedTimeframe === 'month' ? 'CO₂ Saved This Month' :
                                            selectedTimeframe === 'year' ? 'CO₂ Saved This Year' : 'Total CO₂ Saved'}
                            </p>
                        </div>
                    </motion.div>

                    {/* Environmental Impact */}
                    <motion.div
                        className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200"
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 1.0, duration: 0.6 }}
                    >
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Environmental Impact</h2>
                        <div className="space-y-4">
                            {environmentalImpact.map((impact, index) => (
                                <motion.div
                                    key={impact.metric}
                                    className="p-4 bg-gray-50 rounded-lg"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 1.2 + index * 0.1, duration: 0.6 }}
                                >
                                    <div className="text-2xl font-bold text-green-600 mb-1">{impact.value}</div>
                                    <div className="text-sm font-medium text-gray-900 mb-1">{impact.metric}</div>
                                    <div className="text-xs text-gray-600">{impact.description}</div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>

                {/* Project Performance Table */}
                <motion.div
                    className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.4, duration: 0.6 }}
                >
                    <div className="p-8 border-b border-gray-200">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Project Performance</h2>
                        <p className="text-gray-600">Real-time performance metrics for all active projects</p>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Project</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Energy Produced</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">CO₂ Saved</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Payout</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Efficiency</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {projectPerformance.map((project, index) => (
                                    <motion.tr
                                        key={project.name}
                                        className="hover:bg-gray-50"
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 1.6 + index * 0.1, duration: 0.6 }}
                                    >
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-gray-900">{project.name}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-900">{project.energyProduced.toLocaleString()} kWh</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-green-600">{project.co2Saved.toLocaleString()} kg</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-blue-600">${project.payout.toLocaleString()}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center">
                                                <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                                                    <div
                                                        className="bg-green-600 h-2 rounded-full"
                                                        style={{ width: `${project.efficiency}%` }}
                                                    ></div>
                                                </div>
                                                <span className="text-sm text-gray-900">{project.efficiency}%</span>
                                            </div>
                                        </td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </motion.div>

                {/* Investment Tracking Visualizations */}
                <div className="grid lg:grid-cols-2 gap-8 mb-16">
                    {/* Investment Allocation Chart */}
                    <motion.div
                        className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.4, duration: 0.6 }}
                    >
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-900">Investment Allocation</h2>
                            <div className="text-2xl font-bold text-gray-900">$12.3K</div>
                        </div>

                        <div className="space-y-4 mb-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <div className="w-3 h-3 bg-purple-600 rounded-full mr-3"></div>
                                    <span className="text-sm text-gray-600">Solar Projects</span>
                                </div>
                                <span className="text-sm font-medium text-gray-900">$2.3K</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                                    <span className="text-sm text-gray-600">Wind Projects</span>
                                </div>
                                <span className="text-sm font-medium text-gray-900">$1.6K</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                                    <span className="text-sm text-gray-600">Hydro Projects</span>
                                </div>
                                <span className="text-sm font-medium text-gray-900">$1.1K</span>
                            </div>
                        </div>

                        {/* Bar Chart */}
                        <div className="flex items-end justify-between h-32 space-x-2">
                            <div className="flex flex-col items-center">
                                <div className="w-8 bg-purple-600 rounded-t mb-2" style={{ height: '60%' }}></div>
                                <div className="w-8 bg-purple-400 rounded-t" style={{ height: '40%' }}></div>
                                <span className="text-xs text-gray-500 mt-2">Solar</span>
                            </div>
                            <div className="flex flex-col items-center">
                                <div className="w-8 bg-blue-500 rounded-t mb-2" style={{ height: '45%' }}></div>
                                <div className="w-8 bg-blue-300 rounded-t" style={{ height: '35%' }}></div>
                                <span className="text-xs text-gray-500 mt-2">Wind</span>
                            </div>
                            <div className="flex flex-col items-center">
                                <div className="w-8 bg-green-500 rounded-t mb-2" style={{ height: '35%' }}></div>
                                <div className="w-8 bg-green-300 rounded-t" style={{ height: '25%' }}></div>
                                <span className="text-xs text-gray-500 mt-2">Hydro</span>
                            </div>
                            <div className="flex flex-col items-center">
                                <div className="w-8 bg-orange-500 rounded-t mb-2" style={{ height: '30%' }}></div>
                                <div className="w-8 bg-orange-300 rounded-t" style={{ height: '20%' }}></div>
                                <span className="text-xs text-gray-500 mt-2">Storage</span>
                            </div>
                        </div>
                    </motion.div>

                    {/* Revenue Performance Chart */}
                    <motion.div
                        className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.5, duration: 0.6 }}
                    >
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-900">Revenue Performance</h2>
                            <div className="flex items-center text-red-600">
                                <TrendingUp className="w-4 h-4 mr-1 rotate-180" />
                                <span className="text-sm font-medium">-2.3%</span>
                            </div>
                        </div>

                        <div className="text-3xl font-bold text-gray-900 mb-2">$128.7K</div>
                        <div className="text-sm text-gray-600 mb-6">Complete Revenue This Year</div>

                        {/* Line Chart */}
                        <div className="relative h-32">
                            <svg className="w-full h-full" viewBox="0 0 300 100">
                                <defs>
                                    <linearGradient id="revenueGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                        <stop offset="0%" stopColor="#10B981" stopOpacity="0.3" />
                                        <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
                                    </linearGradient>
                                </defs>
                                <path
                                    d="M 10,80 Q 50,60 100,40 T 200,30 T 290,25"
                                    stroke="#10B981"
                                    strokeWidth="2"
                                    fill="none"
                                />
                                <path
                                    d="M 10,80 Q 50,60 100,40 T 200,30 T 290,25 L 290,100 L 10,100 Z"
                                    fill="url(#revenueGradient)"
                                />
                                <circle cx="100" cy="40" r="3" fill="#10B981" />
                                <text x="100" y="35" className="text-xs fill-gray-600">21.2K</text>
                            </svg>
                        </div>

                        <div className="flex justify-between text-xs text-gray-500 mt-2">
                            <span>Jan</span>
                            <span>Feb</span>
                            <span>Mar</span>
                            <span>Apr</span>
                            <span>May</span>
                            <span>Jun</span>
                            <span>Jul</span>
                            <span>Aug</span>
                        </div>
                    </motion.div>
                </div>

                {/* Portfolio Performance Charts */}
                <div className="grid lg:grid-cols-2 gap-8 mb-16">
                    {/* Asset Generation Chart */}
                    <motion.div
                        className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.6, duration: 0.6 }}
                    >
                        <div className="mb-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-2">Portfolio Growth</h2>
                            <p className="text-sm text-gray-600">Your investment portfolio performance</p>
                        </div>

                        <div className="text-3xl font-bold text-gray-900 mb-2">$128.7K</div>
                        <div className="flex items-center text-green-600 mb-6">
                            <TrendingUp className="w-4 h-4 mr-1" />
                            <span className="text-sm font-medium">+3.4%</span>
                            <span className="text-xs text-gray-500 ml-2">20 Oct 00:00</span>
                        </div>

                        {/* Line Chart with Area */}
                        <div className="relative h-32">
                            <svg className="w-full h-full" viewBox="0 0 300 100">
                                <defs>
                                    <linearGradient id="portfolioGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                        <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3" />
                                        <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
                                    </linearGradient>
                                </defs>
                                <path
                                    d="M 10,70 Q 50,50 100,45 T 200,35 T 290,30"
                                    stroke="#3B82F6"
                                    strokeWidth="2"
                                    fill="none"
                                />
                                <path
                                    d="M 10,70 Q 50,50 100,45 T 200,35 T 290,30 L 290,100 L 10,100 Z"
                                    fill="url(#portfolioGradient)"
                                />
                                <circle cx="200" cy="35" r="3" fill="#3B82F6" />
                            </svg>
                        </div>
                    </motion.div>

                    {/* Engagement Metrics Chart */}
                    <motion.div
                        className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.7, duration: 0.6 }}
                    >
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">Investment Activity</h2>
                                <div className="flex items-center space-x-4 mt-2">
                                    <span className="text-sm text-gray-600">New Investors: <span className="font-medium text-gray-900">321</span></span>
                                    <span className="text-sm text-gray-600">Growth: <span className="font-medium text-green-600">8.5%</span></span>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-2xl font-bold text-gray-900">$11.6K</div>
                                <div className="text-sm text-gray-600">Monthly Returns</div>
                            </div>
                        </div>

                        {/* Time Filter Buttons */}
                        <div className="flex space-x-2 mb-6">
                            {['Day', 'Week', 'Month', 'Year'].map((period) => (
                                <button
                                    key={period}
                                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${period === 'Month'
                                            ? 'bg-green-600 text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                >
                                    {period}
                                </button>
                            ))}
                        </div>

                        {/* Line Chart */}
                        <div className="relative h-32">
                            <svg className="w-full h-full" viewBox="0 0 300 100">
                                <defs>
                                    <linearGradient id="activityGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                        <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.3" />
                                        <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0" />
                                    </linearGradient>
                                </defs>
                                <path
                                    d="M 10,80 Q 50,70 100,60 T 200,45 T 290,40"
                                    stroke="#8B5CF6"
                                    strokeWidth="2"
                                    fill="none"
                                />
                                <path
                                    d="M 10,80 Q 50,70 100,60 T 200,45 T 290,40 L 290,100 L 10,100 Z"
                                    fill="url(#activityGradient)"
                                />
                                <circle cx="200" cy="45" r="3" fill="#10B981" />
                                <text x="200" y="40" className="text-xs fill-gray-600">21.2K</text>
                            </svg>
                        </div>
                    </motion.div>
                </div>

                {/* Call to Action */}
                <motion.div
                    className="text-center"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.8, duration: 0.6 }}
                >
                    <div className="bg-white p-8 md:p-12 rounded-2xl shadow-lg border border-gray-200 max-w-4xl mx-auto">
                        <h2 className="text-3xl font-bold mb-4 text-gray-900">
                            Your Impact Matters
                        </h2>
                        <p className="text-lg text-gray-600 mb-8">
                            Every investment contributes to a cleaner, more sustainable future.
                            Track your impact and see the difference you're making.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <motion.button
                                className="bg-green-600 text-white px-8 py-4 rounded-lg font-medium hover:bg-green-700 transition-colors"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                View My Investments
                            </motion.button>
                            <motion.button
                                className="bg-gray-100 text-gray-700 px-8 py-4 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                Download Report
                            </motion.button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
